/**
 * DEMO-ONLY: Users with assigned affiliates see those affiliates across
 * My Account portal lists (MVP 8.2.1) — contracts, devices, orders, invoices, quotes.
 * Shopping context (active customer) does not hide other assigned affiliates here.
 * Affiliate customer folders (Beta/Gamma) have no contracts/devices fixtures; paths remap to home.
 */
import { getDiscoveryUser } from './presidio-mock-discovery';
import {
  PRESIDIO_DISCOVERY_AFFILIATES,
  PresidioDiscoveryAffiliate,
} from '../../presidio-discovery/models/presidio-discovery.model';
import {
  PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO,
  PRESIDIO_MOCK_SALES_REP_ACCOUNTS,
  getPresidioMockActiveLogin,
  isPresidioMockSalesRepLogin,
} from './presidio-mock-session';
import { formatQuotePdfFilename } from 'src/app/extensions/quoting/utils/presidio-quote-export';

import {
  buildPresidioQuoteDetailFromEnrichment,
  buildPresidioQuoteListElement,
  resolvePresidioInboundQuoteStatus,
  PresidioQuoteEnrichment,
} from './presidio-mock-quotes';

export function getCurrentDiscoveryAdministratorAffiliates(): PresidioDiscoveryAffiliate[] {
  const user = getDiscoveryUser(getPresidioMockActiveLogin());
  if (!user || user.primaryRole !== 'administrator') {
    return [];
  }
  return getAssignedAffiliatesForUser(user.affiliateCustomerNos);
}

/**
 * Affiliates assigned to the signed-in user (any role) — used for quote affiliate
 * visibility when the user can access more than one affiliate (MVP 8.2.1).
 */
export function getCurrentUserAssignedAffiliates(): PresidioDiscoveryAffiliate[] {
  const user = getDiscoveryUser(getPresidioMockActiveLogin());
  if (!user) {
    return [];
  }
  return getAssignedAffiliatesForUser(user.affiliateCustomerNos);
}

function getAssignedAffiliatesForUser(customerNos?: string[]): PresidioDiscoveryAffiliate[] {
  const nos = customerNos || [];
  if (!nos.length) {
    return [];
  }
  return PRESIDIO_DISCOVERY_AFFILIATES.filter(a => nos.includes(a.customerNo));
}

export function isCurrentUserDiscoveryAdministrator(): boolean {
  return getDiscoveryUser(getPresidioMockActiveLogin())?.primaryRole === 'administrator';
}

/** Assigned affiliate company names for list filtering. */
export function getAdminAssignedAffiliateCompanyNames(): string[] {
  return getCurrentDiscoveryAdministratorAffiliates().map(a => a.companyName);
}

/** Company names for quotes when the user has multi-affiliate access. */
export function getUserAssignedAffiliateCompanyNames(): string[] {
  return getCurrentUserAssignedAffiliates().map(a => a.companyName);
}

/**
 * Affiliates visible on Contracts / Devices (and related portal lists) for the
 * signed-in demo user — discovery assignments, or all sales-rep accounts for Riley.
 */
export function getPortalVisibleAffiliates(): PresidioDiscoveryAffiliate[] {
  const assigned = getCurrentUserAssignedAffiliates();
  if (assigned.length) {
    return assigned;
  }
  if (isPresidioMockSalesRepLogin()) {
    return PRESIDIO_MOCK_SALES_REP_ACCOUNTS.map(a => ({
      customerNo: a.customerNo,
      companyName: a.companyName,
    }));
  }
  // Logged-in home-only shoppers still get Acme stamped on untagged rows.
  if (getPresidioMockActiveLogin()) {
    return PRESIDIO_DISCOVERY_AFFILIATES.filter(a => a.customerNo === PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO);
  }
  return [];
}

/**
 * Keep My Account portal fixtures under the home customer so shopping as an
 * affiliate does not 404 devices/contracts/reports/workitems (Beta/Gamma have no trees).
 */
export function rewriteAdminMyAccountCustomerPath(path: string): string | null {
  const known = PRESIDIO_MOCK_SALES_REP_ACCOUNTS.map(a => escapeRegExp(a.customerNo)).join('|');
  const match = new RegExp(
    `^customers/(?:-|${known})/(devices|contracts|reports|workitems)(/.*)?$`,
    'i'
  ).exec(path);
  if (!match) {
    return null;
  }
  // Remap whenever we can resolve portal affiliates (incl. sales-rep / single-Acme).
  if (!getPortalVisibleAffiliates().length && !getPresidioMockActiveLogin()) {
    return null;
  }
  return `customers/${PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO}/${match[1]}${match[2] || ''}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function allowedCompanySet(): Set<string> | null {
  const names = getPortalVisibleAffiliates().map(a => a.companyName);
  return names.length ? new Set(names) : null;
}

function companyAllowed(allowed: Set<string>, company?: string | null): boolean {
  const name = (company || '').trim();
  if (!name) {
    // Untagged home-account rows count as Acme for demo admins who have Acme.
    return allowed.has('Acme Healthcare');
  }
  return allowed.has(name);
}

/** Extra order rows for affiliates (merged into custom-orders list). */
export function buildAdminAffiliateOrderRows(): Record<string, unknown>[] {
  const affiliates = getPortalVisibleAffiliates();
  const rows: Record<string, unknown>[] = [];
  for (const aff of affiliates) {
    if (aff.customerNo === PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO) {
      continue;
    }
    const suffix = aff.customerNo === 'PRESIDIO-AFF-BETA' ? 'BETA' : 'GAMMA';
    const doc = aff.customerNo === 'PRESIDIO-AFF-BETA' ? '1000601' : '1000602';
    rows.push({
      id: `MOCK-ORD-${suffix}-001`,
      documentNumber: doc,
      creationDate: aff.customerNo === 'PRESIDIO-AFF-BETA' ? '2026-08-05T14:00:00Z' : '2026-07-22T11:00:00Z',
      status: 'Shipped',
      statusCode: 'SHIPPED',
      invoiceToAddress: 'urn_addr_inv',
      commonShipToAddress: 'urn_addr_ship',
      commonShippingMethod: 'STD_GROUND',
      lineItems: ['li-1'],
      externalOrderReference: `PO-${suffix}-100`,
      presidioTitle:
        aff.customerNo === 'PRESIDIO-AFF-BETA'
          ? 'Plant floor refresh — Beta Manufacturing'
          : 'Campus Wi-Fi — Gamma Education',
      quoteNumber: `Q-${suffix}-10`,
      shipToLocation:
        aff.customerNo === 'PRESIDIO-AFF-BETA' ? 'Beta Plant — Dock A' : 'Gamma Campus — Bldg 2',
      affiliatedCompany: aff.companyName,
      hidden: false,
      billToCode: suffix,
      hasFinderFee: false,
      totals: {
        grandTotal: {
          gross: { value: aff.customerNo === 'PRESIDIO-AFF-BETA' ? 18200 : 9600, currency: 'USD' },
          net: { value: aff.customerNo === 'PRESIDIO-AFF-BETA' ? 18200 : 9600, currency: 'USD' },
          tax: { value: 0, currency: 'USD' },
        },
        itemTotal: {
          gross: { value: aff.customerNo === 'PRESIDIO-AFF-BETA' ? 18200 : 9600, currency: 'USD' },
          net: { value: aff.customerNo === 'PRESIDIO-AFF-BETA' ? 18200 : 9600, currency: 'USD' },
        },
      },
      customer: aff.customerNo,
      purchaseCurrency: 'USD',
      totalProductQuantity: 1,
    });
  }
  return rows;
}

export function buildAdminAffiliateInvoiceRows(): Record<string, unknown>[] {
  const affiliates = getPortalVisibleAffiliates();
  const rows: Record<string, unknown>[] = [];
  for (const aff of affiliates) {
    if (aff.customerNo === PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO) {
      continue;
    }
    const isBeta = aff.customerNo === 'PRESIDIO-AFF-BETA';
    const invNo = isBeta ? 'INV-BETA-01' : 'INV-GAMMA-01';
    rows.push({
      name: invNo,
      type: 'Invoice',
      invoiceNo: invNo,
      invoiceType: 'Standard',
      invoiceEnteredDate: isBeta ? 1722816000000 : 1721606400000,
      invoiceDueDate: isBeta ? 1725408000000 : 1724198400000,
      invoiceStatus: 'O',
      invoiceReferenceNumber: isBeta ? 'PO-BETA-100' : 'PO-GAMMA-100',
      invoiceTransactionDate: isBeta ? 1722816000000 : 1721606400000,
      customerId: aff.customerNo,
      customerName: aff.companyName,
      customerDivision: 'IT',
      channelType: 'Web',
      channelTypeDescription: 'Web',
      invoiceAmount: isBeta ? 18200 : 9600,
      amountApplied: 0,
      amountDiscounted: 0,
      amountBalance: isBeta ? 18200 : 9600,
      poNumber: isBeta ? 'PO-BETA-100' : 'PO-GAMMA-100',
      storeNumber: '01',
      orderNumber: isBeta ? '1000601' : '1000602',
      orderId: isBeta ? 'MOCK-ORD-BETA-001' : 'MOCK-ORD-GAMMA-001',
      aroTerms: 'Net 30',
      title: isBeta ? 'Plant floor refresh — Beta Manufacturing' : 'Campus Wi-Fi — Gamma Education',
      billToAddress: `${aff.companyName}`,
      shipToAddress: isBeta ? 'Beta Plant — Dock A' : 'Gamma Campus — Bldg 2',
      accountManager: 'Sam Rivera',
      icm: `ICM-${isBeta ? 'BETA' : 'GAMMA'}-001`,
      contractVehicle: 'CV-2026-AFF',
      subtotal: isBeta ? 18200 : 9600,
      taxTotal: 0,
      usageReportAvailable: false,
      quoteNumber: isBeta ? 'Q-BETA-10' : 'Q-GAMMA-10',
      quoteId: isBeta ? 'MOCK-Q-BETA-001' : 'MOCK-Q-GAMMA-001',
      attachments: isBeta
        ? [
            { id: 'att-inv-beta-1', name: 'INV-BETA-01.pdf' },
            { id: 'att-inv-beta-2', name: 'Beta-plant-packing-slip.pdf' },
          ]
        : [{ id: 'att-inv-gamma-1', name: 'INV-GAMMA-01.pdf' }],
      lineItems: [
        {
          partNumber: isBeta ? 'CISCO-C9300' : 'PRS-WIFI-AP',
          description: isBeta ? 'Catalyst 9300' : 'Wi-Fi access point',
          unitPrice: isBeta ? 18200 : 9600,
          quantity: 1,
          extPrice: isBeta ? 18200 : 9600,
          uom: 'EA',
          tax: 0,
        },
      ],
    });
  }
  return rows;
}

export function buildAdminAffiliateDeviceRows(): Record<string, unknown>[] {
  const affiliates = getPortalVisibleAffiliates();
  const rows: Record<string, unknown>[] = [];
  for (const aff of affiliates) {
    if (aff.customerNo === PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO) {
      continue;
    }
    const isBeta = aff.customerNo === 'PRESIDIO-AFF-BETA';
    rows.push({
      id: isBeta ? 'DEV-BETA-1' : 'DEV-GAMMA-1',
      partNumber: isBeta ? 'C9300-24T' : 'C9120AXI',
      serialNumber: isBeta ? 'FCW-BETA-001' : 'FCW-GAMMA-001',
      description: isBeta ? 'Catalyst 9300 24-port (Beta)' : 'Catalyst 9120AX (Gamma)',
      manufacturer: 'Cisco',
      deviceType: 'Chassis',
      serviceLevel: '8x5xNBD',
      contractNumber: isBeta ? 'SN-BETA-100' : 'SN-GAMMA-100',
      contractStatus: 'Active',
      contractStart: '2026-01-01',
      siteId: isBeta ? 'SITE-BETA-01' : 'SITE-GAMMA-01',
      siteAddress: isBeta ? '400 Industrial Pkwy, Detroit, MI' : '50 Campus Dr, Boston, MA',
      owner: 'Jordan Hale',
      labels: [aff.companyName],
      contractEnd: '2027-01-01',
      lastDateOfSupport: '2029-01-01',
      endOfSale: '2028-01-01',
      warrantyType: 'Hardware',
      warrantyEndDate: '2027-06-01',
      itemQuantity: 1,
      instanceNumber: isBeta ? 'INST-BETA-1' : 'INST-GAMMA-1',
      affiliateCompany: aff.companyName,
      manufacturerDataUpdatedAt: '2026-09-16',
    });
  }
  return rows;
}

export function buildAdminAffiliateContractRows(): Record<string, unknown>[] {
  const affiliates = getPortalVisibleAffiliates();
  const rows: Record<string, unknown>[] = [];
  for (const aff of affiliates) {
    if (aff.customerNo === PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO) {
      continue;
    }
    const isBeta = aff.customerNo === 'PRESIDIO-AFF-BETA';
    rows.push({
      id: isBeta ? 'MC-BETA-1' : 'MC-GAMMA-1',
      contractNumber: isBeta ? 'SN-BETA-100' : 'SN-GAMMA-100',
      manufacturer: 'Cisco',
      serviceLevel: '8x5xNBD',
      type: 'SmartNet',
      status: 'Active',
      deviceCount: isBeta ? 6 : 12,
      startDate: '2026-01-01',
      endDate: '2027-01-01',
      source: 'ICM',
      affiliateCompany: aff.companyName,
      contractCountPerManufacturer: 1,
      contractCountPerServiceLevel: 1,
      totalDevicesPerManufacturer: isBeta ? 6 : 12,
      totalDevicesPerServiceLevel: isBeta ? 6 : 12,
    });
  }
  return rows;
}

const AFFILIATE_QUOTE_VALID_TO = Date.parse('2026-12-31T23:59:59.000Z');

/** DEMO: Beta affiliate quotes with varied workflow histories (audit trail). */
function buildBetaAffiliateQuotes(companyName: string): PresidioQuoteEnrichment[] {
  const addresses = [
    {
      type: 'From' as const,
      name: 'Riley Sales',
      company: 'Presidio Networked Solutions LLC',
      addressLines: ['100 Presidio Way'],
      cityStateZip: 'Austin, TX 78701',
      email: 'riley.sales@presidio.com',
    },
    {
      type: 'To' as const,
      company: companyName,
      accountCode: 'BETA001',
      name: 'Alex Rivera',
      phone: '(512) 555-4400',
    },
    {
      type: 'BillTo' as const,
      company: companyName,
      addressLines: ['400 Industrial Pkwy'],
      cityStateZip: 'Houston, TX 77002',
      contactName: 'Alex Rivera',
      email: 'alex.rivera@acme.com',
    },
    {
      type: 'ShipTo' as const,
      company: companyName,
      addressLines: ['400 Industrial Pkwy'],
      cityStateZip: 'Houston, TX 77002',
      contactName: 'Plant Receiving',
    },
  ];
  return [
    {
      id: 'MOCK-Q-BETA-001',
      number: 'Q-BETA-10',
      displayName: 'Beta plant switching quote',
      customerVisible: true,
      quoteCategory: 'STANDARD',
      creationDate: 1722816000000,
      validFromDate: 1722816000000,
      validToDate: AFFILIATE_QUOTE_VALID_TO,
      itemCount: 1,
      pricingRequestNumber: 'PR-BETA-1',
      invoiceNumber: 'INV-BETA-01',
      contact: 'Alex Rivera',
      contractVehicle: 'SEWP V',
      affiliatedCompany: companyName,
      accountManager: 'Eric Mohr',
      insideSalesRep: 'Riley Sales',
      addresses,
      attachments: [
        { name: formatQuotePdfFilename('Q-BETA-10') },
        { name: 'Plant-switching-diagram.pdf' },
        { name: 'Rack-elevation.docx' },
      ],
      approvalSteps: [
        {
          name: 'Riley Sales',
          role: 'Presidio Sales Team',
          status: 'Approved',
          date: '2024-08-04T13:10:00.000Z',
        },
        {
          name: 'Morgan Chen',
          role: 'Quote Approver Level I',
          status: 'Approved',
          date: '2024-08-04T14:02:18.000Z',
        },
        {
          name: 'Jordan Hale',
          role: 'Quote Approver',
          status: 'Approved',
          date: '2024-08-05T09:40:00.000Z',
        },
      ],
      partNumbers: ['CISCO-C9300'],
      sellerComment: `Prepared for ${companyName}.`,
      lineMeta: [
        {
          productSKU: 'CISCO-C9300',
          lineNumber: 1,
          manufacturer: 'Cisco',
          manufacturerPartNumber: 'CISCO-C9300',
          lineType: 'Hardware',
          name: 'Catalyst 9300',
          unitPrice: 18200,
          quantity: 1,
        },
      ],
      totalValue: 18200,
    },
    {
      id: 'MOCK-Q-BETA-002',
      number: 'Q-BETA-11',
      displayName: 'Beta Wi‑Fi controllers',
      customerVisible: true,
      quoteCategory: 'STANDARD',
      creationDate: Date.parse('2026-04-10T12:00:00.000Z'),
      validFromDate: Date.parse('2026-04-10T12:00:00.000Z'),
      validToDate: AFFILIATE_QUOTE_VALID_TO,
      itemCount: 1,
      contact: 'Alex Rivera',
      contractVehicle: 'Cisco EA',
      affiliatedCompany: companyName,
      accountManager: 'Vincent F Trama',
      insideSalesRep: 'Riley Sales',
      addresses,
      attachments: [
        { name: formatQuotePdfFilename('Q-BETA-11') },
        { name: 'Beta-WiFi-BOM.pdf' },
        { name: 'Controller-datasheet.pdf' },
      ],
      approvalSteps: [
        {
          name: 'Riley Sales',
          role: 'Presidio Sales Team',
          status: 'Approved',
          date: '2026-04-10T12:30:00.000Z',
        },
        {
          name: 'Morgan Chen',
          role: 'Quote Approver Level I',
          status: 'Approved',
          date: '2026-04-10T15:12:44.000Z',
        },
        {
          name: 'Jordan Hale',
          role: 'Quote Approver',
          status: 'Pending',
        },
      ],
      partNumbers: ['CISCO-C9300'],
      sellerComment: 'Waiting on final approver for Beta plant Wi‑Fi.',
      lineMeta: [
        {
          productSKU: 'CISCO-C9300',
          lineNumber: 1,
          manufacturer: 'Cisco',
          manufacturerPartNumber: 'CISCO-C9300',
          lineType: 'Hardware',
          name: 'Catalyst 9300 (Wi‑Fi uplink)',
          unitPrice: 16400,
          quantity: 2,
        },
      ],
      totalValue: 32800,
    },
    {
      id: 'MOCK-Q-BETA-003',
      number: 'Q-BETA-09',
      displayName: 'Beta spare parts',
      customerVisible: true,
      quoteCategory: 'STANDARD',
      creationDate: Date.parse('2025-11-01T12:00:00.000Z'),
      validFromDate: Date.parse('2025-11-01T12:00:00.000Z'),
      validToDate: Date.parse('2025-12-15T23:59:59.000Z'),
      itemCount: 1,
      contact: 'Alex Rivera',
      affiliatedCompany: companyName,
      accountManager: 'Eric Mohr',
      insideSalesRep: 'Riley Sales',
      addresses: addresses.slice(0, 2),
      attachments: [{ name: formatQuotePdfFilename('Q-BETA-09') }, { name: 'Spare-parts-list.xlsx' }],
      rejected: true,
      approvalSteps: [
        {
          name: 'Riley Sales',
          role: 'Presidio Sales Team',
          status: 'Approved',
          date: '2025-11-02T10:00:00.000Z',
        },
        {
          name: 'Jordan Hale',
          role: 'Quote Approver',
          status: 'Rejected',
          date: '2025-11-03T16:22:10.000Z',
          notes: 'Defer spare buy until Q1 — use existing stock.',
        },
      ],
      partNumbers: ['SUPPORT-3YR'],
      sellerComment: 'Rejected — see workflow notes.',
      lineMeta: [
        {
          productSKU: 'SUPPORT-3YR',
          lineNumber: 1,
          manufacturer: 'Presidio',
          manufacturerPartNumber: 'SUPPORT-3YR',
          lineType: 'Subscription',
          name: 'Spare support retainer',
          unitPrice: 4500,
          quantity: 1,
        },
      ],
      totalValue: 4500,
    },
  ];
}

/** Quote enrichments for affiliate demo rows (fed into quote catalog visibility). */
export function buildAdminAffiliateQuoteEnrichments(): PresidioQuoteEnrichment[] {
  // Any user with assigned affiliates (not admin-only) — quote affiliate column (MVP 8.2.1).
  const affiliates = getCurrentUserAssignedAffiliates();
  const out: PresidioQuoteEnrichment[] = [];
  for (const aff of affiliates) {
    if (aff.customerNo === PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO) {
      continue;
    }
    const isBeta = aff.customerNo === 'PRESIDIO-AFF-BETA';
    if (isBeta) {
      out.push(...buildBetaAffiliateQuotes(aff.companyName));
      continue;
    }
    out.push({
      id: 'MOCK-Q-GAMMA-001',
      number: 'Q-GAMMA-10',
      displayName: 'Gamma campus Wi-Fi quote',
      customerVisible: true,
      quoteCategory: 'STANDARD',
      creationDate: 1721606400000,
      validFromDate: 1721606400000,
      validToDate: AFFILIATE_QUOTE_VALID_TO,
      itemCount: 1,
      pricingRequestNumber: 'PR-GAMMA-1',
      contact: 'Alex Rivera',
      contractVehicle: 'SEWP V',
      affiliatedCompany: aff.companyName,
      accountManager: 'Vincent F Trama',
      insideSalesRep: 'Riley Sales',
      approvalSteps: [
        {
          name: 'Riley Sales',
          role: 'Presidio Sales Team',
          status: 'Approved',
          date: '2024-07-22T11:00:00.000Z',
        },
        {
          name: 'Sam Patel',
          role: 'Account Owner',
          status: 'Approved',
          date: '2024-07-22T11:45:00.000Z',
        },
      ],
      partNumbers: ['PRS-WIFI-AP'],
      sellerComment: `Prepared for ${aff.companyName}.`,
      lineMeta: [
        {
          productSKU: 'PRS-WIFI-AP',
          lineNumber: 1,
          manufacturer: 'Cisco',
          manufacturerPartNumber: 'PRS-WIFI-AP',
          lineType: 'Hardware',
          name: 'Wi-Fi AP',
          unitPrice: 9600,
          quantity: 1,
        },
      ],
      totalValue: 9600,
    });
  }
  return out;
}

/**
 * Overlay My Account list GET bodies so admins see all assigned affiliates.
 */
export function applyAdminMyAccountAffiliateOverlay(
  restPath: string,
  method: string,
  body: unknown
): unknown {
  if (method.toUpperCase() !== 'GET' || !body || typeof body !== 'object') {
    return body;
  }
  const allowed = allowedCompanySet();
  if (!allowed) {
    return body;
  }

  const path = restPath.split('?')[0];

  if (path === 'custom-orders' || path.startsWith('custom-orders')) {
    return mergeListData(
      stampAffiliateField(body, 'data', 'affiliatedCompany', 'Acme Healthcare'),
      buildAdminAffiliateOrderRows(),
      'data',
      row => companyAllowed(allowed, String((row as { affiliatedCompany?: string }).affiliatedCompany || ''))
    );
  }

  if (path === 'invoices' || path.startsWith('invoices')) {
    return mergeListElements(
      stampAffiliateField(body, 'elements', 'customerName', 'Acme Healthcare'),
      buildAdminAffiliateInvoiceRows(),
      row => companyAllowed(allowed, String((row as { customerName?: string }).customerName || ''))
    );
  }

  if (/\/devices$/i.test(path) || /customers\/[^/]+\/devices$/i.test(path)) {
    return mergeListElements(
      stampAffiliateField(body, 'elements', 'affiliateCompany', 'Acme Healthcare'),
      buildAdminAffiliateDeviceRows(),
      row => {
        const company =
          String((row as { affiliateCompany?: string }).affiliateCompany || '').trim() || 'Acme Healthcare';
        return companyAllowed(allowed, company);
      }
    );
  }

  if (/\/contracts$/i.test(path) || /customers\/[^/]+\/contracts$/i.test(path)) {
    return mergeListElements(
      stampAffiliateField(body, 'elements', 'affiliateCompany', 'Acme Healthcare'),
      buildAdminAffiliateContractRows(),
      row => {
        const company =
          String((row as { affiliateCompany?: string }).affiliateCompany || '').trim() || 'Acme Healthcare';
        return companyAllowed(allowed, company);
      }
    );
  }

  return body;
}

/**
 * Merge affiliate quotes into the visible quote list when the user can access
 * more than one affiliate. Always stamp home-account rows with Acme.
 */
export function applyAdminAffiliateQuoteListOverlay(body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const companyNames = getUserAssignedAffiliateCompanyNames();
  const multiAffiliate = companyNames.length > 1;
  const allowed = multiAffiliate ? new Set(companyNames) : null;

  const attrCompany = (row: Record<string, unknown>): string => {
    const attrs = Array.isArray(row.attributes) ? (row.attributes as { name?: string; value?: unknown }[]) : [];
    const hit = attrs.find(a => a.name === 'affiliatedCompany');
    return String(hit?.value || '').trim();
  };
  const withHomeLabel = (() => {
    const clone = JSON.parse(JSON.stringify(body)) as { elements?: Record<string, unknown>[] };
    clone.elements = (clone.elements || []).map(row => {
      const company = attrCompany(row);
      if (company) {
        return row;
      }
      const attrs = Array.isArray(row.attributes) ? [...(row.attributes as object[])] : [];
      attrs.push({ name: 'affiliatedCompany', type: 'String', value: 'Acme Healthcare' });
      return { ...row, attributes: attrs };
    });
    return clone;
  })();

  if (!allowed) {
    // Single-affiliate users: keep home quotes; no affiliate column extras required.
    return withHomeLabel;
  }

  const extras = buildAdminAffiliateQuoteEnrichments().map(buildPresidioQuoteListElement);
  return mergeListElements(withHomeLabel, extras, row =>
    companyAllowed(allowed, attrCompany(row as Record<string, unknown>) || 'Acme Healthcare')
  );
}

/**
 * Detail for affiliate contracts that exist only in the list overlay (no static fixture).
 * DEMO-ONLY — Beta/Gamma rows from `buildAdminAffiliateContractRows`.
 */
export function getAdminAffiliateContractDetail(contractId: string): Record<string, unknown> | null {
  const id = String(contractId || '').trim();
  if (!id) {
    return null;
  }
  const row = buildAdminAffiliateContractRows().find(r => String(r.id) === id);
  return row ? { ...row } : null;
}

/** Detail enrichment for demo affiliate quotes (not in static catalog). */
export function getAdminAffiliateQuoteEnrichment(quoteId: string): PresidioQuoteEnrichment | undefined {
  // Available to any user who is assigned the affiliate for this quote.
  return buildAdminAffiliateQuoteEnrichments().find(q => q.id === quoteId);
}

/** Full quote detail payload for affiliate demo quotes (no static fixture file). */
export function buildAdminAffiliateQuoteDetail(quoteId: string): Record<string, unknown> | null {
  const enrichment = getAdminAffiliateQuoteEnrichment(quoteId);
  if (!enrichment) {
    return null;
  }
  const body = buildPresidioQuoteDetailFromEnrichment(enrichment);
  const status = resolvePresidioInboundQuoteStatus(enrichment);
  return {
    ...body,
    accountManager: enrichment.accountManager,
    insideSalesRep: enrichment.insideSalesRep,
    addresses: enrichment.addresses || [],
    attachments: enrichment.attachments || [],
    approvalSteps: enrichment.approvalSteps || [],
    contact: enrichment.contact,
    contractVehicle: enrichment.contractVehicle,
    affiliatedCompany: enrichment.affiliatedCompany,
    pricingRequestNumber: enrichment.pricingRequestNumber,
    salesNotes: enrichment.salesNotes || enrichment.sellerComment,
    pendingApproval: false,
    purchased: status === 'Processing',
    inboundStatus: status,
    subtotal: body.total,
  };
}

/** Ensure home-account rows carry an affiliate label so multi-affiliate lists are scannable. */
function stampAffiliateField(
  body: unknown,
  listKey: 'data' | 'elements',
  field: string,
  fallback: string
): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const clone = JSON.parse(JSON.stringify(body)) as Record<string, unknown>;
  const list = Array.isArray(clone[listKey]) ? (clone[listKey] as Record<string, unknown>[]) : [];
  clone[listKey] = list.map(row => {
    const current = String(row[field] || '').trim();
    if (current && current !== 'Presidio Internal') {
      return row;
    }
    if (current === 'Presidio Internal') {
      return row;
    }
    return { ...row, [field]: fallback };
  });
  return clone;
}

function mergeListData(
  body: unknown,
  extras: Record<string, unknown>[],
  key: 'data',
  keep: (row: unknown) => boolean
): unknown {
  const clone = JSON.parse(JSON.stringify(body)) as Record<string, unknown>;
  const existing = Array.isArray(clone[key]) ? (clone[key] as Record<string, unknown>[]) : [];
  const byId = new Map<string, Record<string, unknown>>();
  for (const row of existing) {
    byId.set(String(row.id || ''), row);
  }
  for (const row of extras) {
    byId.set(String(row.id || ''), row);
  }
  const merged = [...byId.values()].filter(keep);
  clone[key] = merged;
  if (clone.info && typeof clone.info === 'object') {
    (clone.info as { total?: number }).total = merged.length;
  }
  return clone;
}

function elementRowId(row: Record<string, unknown>): string {
  // Quote list Links use itemId/title (ICM), not id — missing that collapsed every row to "".
  return String(row.id || row.itemId || row.title || row.invoiceNo || row.name || '');
}

function mergeListElements(
  body: unknown,
  extras: Record<string, unknown>[],
  keep: (row: unknown) => boolean
): unknown {
  const clone = JSON.parse(JSON.stringify(body)) as Record<string, unknown>;
  const existing = Array.isArray(clone.elements) ? (clone.elements as Record<string, unknown>[]) : [];
  const byId = new Map<string, Record<string, unknown>>();
  for (const row of existing) {
    const id = elementRowId(row);
    if (id) {
      byId.set(id, row);
    }
  }
  for (const row of extras) {
    const id = elementRowId(row);
    if (id) {
      byId.set(id, row);
    }
  }
  const merged = [...byId.values()].filter(keep);
  clone.elements = merged;
  clone.total = merged.length;
  return clone;
}
